/**
 * Where the planner was left, so it opens there next time.
 *
 * A fixed starting point is a pan across the country whenever you plan
 * somewhere that isn't it — and where you were last is a better guess than
 * any constant, because it is usually where the next route is too.
 *
 * Per browser, and deliberately not per route: this is the view you were
 * working in, not a property of anything saved.
 */

const KEY = 'cad.map.lastView';

export interface MapView {
  center: [number, number];
  zoom: number;
}

export function loadLastView(): MapView | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MapView>;
    const center = parsed.center;
    if (
      !Array.isArray(center) ||
      center.length !== 2 ||
      !center.every((n) => typeof n === 'number' && Number.isFinite(n)) ||
      Math.abs(center[0]!) > 180 ||
      Math.abs(center[1]!) > 90 ||
      typeof parsed.zoom !== 'number' ||
      !Number.isFinite(parsed.zoom)
    ) {
      return null;
    }
    return { center: [center[0]!, center[1]!], zoom: parsed.zoom };
  } catch {
    // Private browsing, or something else wrote nonsense to the key. Neither
    // is worth failing to open the planner over.
    return null;
  }
}

export function saveLastView(view: MapView): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(view));
  } catch {
    // Storage full or blocked; the map still works, it just forgets.
  }
}
